import * as THREE from 'three';

/**
 * パスA: WebXR Depth Sensing による実風景オクルージョン(計画書§8.1)。
 *
 * gpu-optimized の深度テクスチャを onBeforeCompile で全マテリアルに注入し、
 * フラグメントごとに「仮想オブジェクトの深度 > 実世界深度」なら discard する。
 * 深度非対応端末では自動的に無効(feature detection)。
 *
 * 注意: WebXR Depth Sensingの実装は端末・Chromeバージョンごとに差異が大きい。
 * Phase 5 で対応Android実機での調整が必須(CLAUDE.md参照)。
 */

interface DepthUniforms {
  uDepthTexture: { value: THREE.Texture | null };
  uUvTransform: { value: THREE.Matrix4 };
  uRawValueToMeters: { value: number };
  uDepthEnabled: { value: number };
  uResolution: { value: THREE.Vector2 };
}

export class DepthOcclusion {
  private uniforms: DepthUniforms = {
    uDepthTexture: { value: null },
    uUvTransform: { value: new THREE.Matrix4() },
    uRawValueToMeters: { value: 0.001 },
    uDepthEnabled: { value: 0 },
    uResolution: { value: new THREE.Vector2(1, 1) },
  };
  private glBinding: XRWebGLBinding | null = null;
  private texture: WebGLTexture | null = null;
  private threeTexture: THREE.Texture | null = null;
  private supported: boolean | null = null;

  /** contentGroup配下の全マテリアルに深度比較シェーダーを注入する */
  attachToGroup(group: THREE.Group): void {
    group.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.name !== 'occluder') {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const m of mats) this.injectShader(m);
      }
    });
  }

  private injectShader(material: THREE.Material): void {
    const uniforms = this.uniforms;
    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, uniforms);
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          /* glsl */ `
          #include <common>
          uniform sampler2D uDepthTexture;
          uniform mat4 uUvTransform;
          uniform float uRawValueToMeters;
          uniform float uDepthEnabled;
          uniform vec2 uResolution;
          `,
        )
        .replace(
          '#include <dithering_fragment>',
          /* glsl */ `
          #include <dithering_fragment>
          if (uDepthEnabled > 0.5) {
            vec2 screenUv = gl_FragCoord.xy / uResolution;
            vec2 depthUv = (uUvTransform * vec4(screenUv, 0.0, 1.0)).xy;
            if (depthUv.x >= 0.0 && depthUv.x <= 1.0 && depthUv.y >= 0.0 && depthUv.y <= 1.0) {
              // luminance-alpha: 16bit値を2チャンネルから復元
              vec2 packedDepth = texture2D(uDepthTexture, depthUv).ra;
              float realDepthMeters = dot(packedDepth, vec2(255.0, 256.0 * 255.0)) * uRawValueToMeters;
              float fragDepthMeters = gl_FragCoord.z / gl_FragCoord.w;
              // 遮蔽は近距離(8m以内)の仮想物のみに適用する。
              // 深度センサーの有効レンジは数mで、空や遠景では最大値を返すため、
              // 上空の機体・落下中の爆弾を無条件比較すると全フラグメントが
              // discardされ「モデルが消える」致命的状態になる。
              if (fragDepthMeters < 8.0 && realDepthMeters > 0.05 && realDepthMeters < 7.5 &&
                  fragDepthMeters > realDepthMeters + 0.1) {
                discard;
              }
            }
          }
          `,
        );
      material.userData.shader = shader;
    };
    material.needsUpdate = true;
  }

  /** 毎フレーム、XRフレームから深度情報を取得してテクスチャを更新する */
  update(renderer: THREE.WebGLRenderer, frame: XRFrame | undefined): void {
    this.uniforms.uDepthEnabled.value = 0;
    if (!frame) return;
    const session = frame.session;
    const refSpace = renderer.xr.getReferenceSpace();
    if (!refSpace) return;
    const pose = frame.getViewerPose(refSpace);
    if (!pose) return;
    const view = pose.views[0];
    if (!view) return;

    // feature detection: getDepthInformation が存在しない/例外を投げる端末では無効化
    if (this.supported === false) return;
    try {
      if (!this.glBinding) {
        const gl = renderer.getContext() as WebGL2RenderingContext;
        this.glBinding = new XRWebGLBinding(session, gl);
      }
      const depthInfo = this.glBinding.getDepthInformation(view);
      if (!depthInfo || !depthInfo.texture) return;

      this.supported = true;
      const gl = renderer.getContext() as WebGL2RenderingContext;
      if (this.texture !== depthInfo.texture) {
        this.texture = depthInfo.texture;
        // WebGLテクスチャをthree.jsのテクスチャとしてラップする
        const tex = new THREE.Texture();
        (renderer.properties.get(tex) as { __webglTexture?: WebGLTexture }).__webglTexture = this.texture;
        tex.flipY = false;
        this.threeTexture = tex;
        void gl;
      }
      this.uniforms.uDepthTexture.value = this.threeTexture;
      const vp = renderer.xr.getSession()?.renderState.baseLayer?.getViewport?.(view);
      if (vp) this.uniforms.uResolution.value.set(vp.width, vp.height);
      this.uniforms.uRawValueToMeters.value = depthInfo.rawValueToMeters;
      this.uniforms.uUvTransform.value.fromArray(depthInfo.normDepthBufferFromNormView.matrix);
      this.uniforms.uDepthEnabled.value = 1;
    } catch {
      this.supported = false;
      console.warn('[DepthOcclusion] この端末は depth-sensing 非対応です。オクルージョンを無効化します。');
    }
  }

  get isActive(): boolean {
    return this.uniforms.uDepthEnabled.value > 0.5;
  }
}
